// Layout for every signed-in surface that isn't an auth/checkout flow:
// /appv2 wizard, /create dashboard, /brand page, /gallery, and the
// /appv2/recreate/:id runtime. Has a responsive left sidebar (auto-
// collapses to a 64px icon rail, expands to a 274px labeled sidebar via a
// hamburger toggle — an overlay drawer on mobile) so the content column
// stays usable at every reasonable monitor size.
//
// Sidebar contents (top → bottom) — IDENTICAL on every route. No
// hide/show behavior per-page; nav structure stays 100% the same so
// the user never has to relearn where things are when they navigate.
//
//   - Logo + v2 chip
//   - WORKSPACE group:
//       + New Creative  (primary CTA pill — always visible, self-navigates
//                    to /create which is a no-op-but-fine if already there)
//       My Brand
//       My Products
//       My Creatives
//   - CONNECT group:
//       Instagram / Facebook / TikTok — Instagram + Facebook ship with
//       real OAuth + post-to-feed; TikTok shows a "Coming soon" modal
//       (replaces LinkedIn, which was vestigial — neither OAuth nor
//       publish ever shipped). Pinterest removed for v1 (not on the
//       launch platform list).
//   - (flex-1 spacer)
//   - ACCOUNT group:
//       Credits chip (gradient, balance + plan)
//       Account
//       (hairline)
//       Sign out (red-tinted hover)
//
// The sidebar's content-facing edge uses .sidebar-right-stroke — a
// vertical gradient (transparent → white → transparent) so it reads as
// a soft glass-edge highlight rather than a flat divider.

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCredits';
import { useBrand } from '@/hooks/useBrand';
import { useConfirm } from '@/hooks/useConfirm';
import { useFacebookConnection } from '@/hooks/useFacebookConnection';
import { useSocialPublishingEnabled, useSchedulingEnabled } from '@/hooks/useFeatureFlags';
import { brandAvatarStyle } from '@/lib/brandThumbnail';
import { Logo } from './Logo';
import {
  PLATFORM_META,
  PLATFORM_PICK_ORDER,
} from '@/pages/schedule/components/platforms';
import {
  listAdStrategistJobs,
  listScheduledPosts,
  startFacebookOAuth,
  disconnectFacebook,
} from '@/lib/api';

interface AppLayoutV2Props {
  /** Optional override for the "+ New Creative" button click. Defaults to
   *  navigate('/create'). Parents with unsaved in-flight state (the
   *  /appv2 wizard) override to first show a confirmation modal. */
  onNewPost?: () => void;
  /** Optional override for the Gallery row click. Defaults to
   *  navigate('/gallery'). */
  onGallery?: () => void;
  /** Optional override for the My Brand row click. Defaults to
   *  navigate('/brand'). */
  onBrand?: () => void;
  /** Optional override for the My Products row click. Defaults to
   *  navigate('/products'). */
  onProducts?: () => void;
  /** Optional override for the Templates row click. Defaults to
   *  navigate('/templates'). */
  onTemplates?: () => void;
  /** Optional override for the Ad Strategist V1 row click. Defaults to
   *  navigate('/ad-strategist-v1'). */
  onAdStrategistV1?: () => void;
  /** Optional override for the Ad Pilot (auto-schedule) row click.
   *  Defaults to navigate('/schedule/auto'). */
  onAdPilot?: () => void;
  /** Optional override for the Calendar (scheduler) row click. Defaults
   *  to navigate('/schedule'). */
  onSchedule?: () => void;
  children: React.ReactNode;
}

// Platforms surfaced in the Connect group. Each opens a "Coming soon"
// modal on click. Brand colors are the official platform colors —
// muted on the bg layer so the row reads as a row, not a colored chip.
interface PlatformDef {
  id: string;
  name: string;
  icon: string;     // Remix Icon class
  color: string;    // Brand hex
}

// PLATFORMS is now DERIVED from the canonical PLATFORM_PICK_ORDER +
// PLATFORM_META in /pages/schedule/components/platforms.ts so the
// sidebar Connect group and the ScheduleModal always show the SAME set
// of platforms. To add/remove a platform across the app, edit that one
// file. (Pre-June 8 the sidebar had its own 3-entry local array which
// drifted from the modal's 4-entry list — fixed.)
//
// FB sidebar icon historically used `ri-facebook-circle-line`; the
// canonical meta uses the same family so no override needed.


export default function AppLayoutV2({
  onNewPost,
  onGallery,
  onBrand,
  onProducts,
  onTemplates,
  onAdStrategistV1,
  onAdPilot,
  onSchedule,
  children,
}: AppLayoutV2Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const SOCIAL_PUBLISHING_ENABLED = useSocialPublishingEnabled();
  const SCHEDULING_ENABLED = useSchedulingEnabled();
  const { balance, planTier, trialEndsAt, loading: creditsLoading } = useCredits();
  const { activeBrand } = useBrand();
  // Jul 24 tiering — Ad Strategist + Ad Pilot are Pro & Business only. Hidden
  // from the sidebar for Free/Starter (the pages themselves also upsell-guard).
  const isPremiumTier = planTier === 'pro' || planTier === 'business';

  // Compute which workspace/account nav item matches the current route.
  // Exact path matches; we deliberately DON'T highlight on sub-routes
  // (e.g. /appv2/recreate/:id is a creation flow, not "browsing the
  // gallery", so nothing in the nav is "active" there). The "+ New Creative"
  // button stays its solid primary pill regardless — it's the always-on
  // CTA, not a route-bound nav item.
  const activePath = location.pathname;

  // Free-trial state: a trialing user carries a paid plan_tier (for access)
  // PLUS a future trial_ends_at. Show a "Trial" badge until it lapses — it
  // self-expires client-side once the date passes, even if the clear-webhook
  // is ever missed. ('free' = signed up, 0 credits, no trial started yet.)
  const isTrialing = !!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now();
  const trialDaysLeft = isTrialing
    ? Math.max(1, Math.ceil((new Date(trialEndsAt!).getTime() - Date.now()) / 86_400_000))
    : 0;
  const planName = planTier.charAt(0).toUpperCase() + planTier.slice(1);
  const planLabel = !user
    ? 'Not signed in'
    : planTier === 'free'
    ? 'Free'
    : isTrialing
    ? `${planName} · Trial`
    : `${planName} plan`;

  const handleNewPost  = onNewPost  ?? (() => navigate('/create'));
  const handleGallery  = onGallery  ?? (() => navigate('/gallery'));
  const handleBrand    = onBrand    ?? (() => navigate('/brand'));
  const handleProducts = onProducts ?? (() => navigate('/products'));
  const handleTemplates = onTemplates ?? (() => navigate('/templates'));
  const handleAdStrategistV1 = onAdStrategistV1 ?? (() => navigate('/ad-strategist-v1'));
  const handleAdPilot = onAdPilot ?? (() => navigate('/schedule/auto'));
  const handleSchedule = onSchedule ?? (() => navigate('/schedule'));

  // Ad Strategist unread badge — pink dot on the sidebar row when there's at
  // least one job created after the user's last_viewed_at on any of their
  // jobs. Cleared when the user visits /ad-strategist (the page calls
  // Ad Strategist unread badge was removed per UX direction. State + fetch
  // intentionally gone — no point burning a /api/ad-strategist/jobs call
  // on every layout mount when the result isn't displayed.

  // Platform-connection modal — opens on Connect-group click, body
  // explains the (future) direct-publishing value prop. State lives
  // inside the layout because every page that uses AppLayoutV2 wants
  // the same behavior; no need to thread through props.
  const [connectingTo, setConnectingTo] = useState<PlatformDef | null>(null);

  // Sidebar open/close (Mobile-Opt) — the rail was icon-only via a pure
  // `min-[1790px]:` breakpoint with NO way to read the labels below 1790px.
  // Now state-driven: the aside carries data-expanded and the rows use
  // group-data-[expanded=true]:* to show labels + widen. Default matches the
  // old breakpoint (expanded on ≥1790 screens) but is toggleable + persisted.
  const [navExpanded, setNavExpanded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    // Mobile/tablet (< lg): the sidebar is an OVERLAY drawer — always start
    // CLOSED so each page loads with content visible. The layout re-mounts per
    // route, so this also auto-closes the drawer after a nav tap. Desktop
    // (>= lg) remembers the user's expand/collapse preference.
    if (window.innerWidth < 1024) return false;
    try {
      const saved = window.localStorage.getItem('nav_expanded');
      if (saved != null) return saved === 'true';
    } catch { /* private mode */ }
    return window.innerWidth >= 1790;
  });
  useEffect(() => {
    try { window.localStorage.setItem('nav_expanded', String(navExpanded)); } catch { /* ignore */ }
  }, [navExpanded]);

  // Ad Calendar badge — count of UPCOMING scheduled posts (a green light that
  // there's something on the calendar). Counts distinct future slots (a post
  // fanned to IG+FB is ONE calendar card, so we dedupe by post+time to match
  // what the calendar shows). Refetched on every route change so it updates
  // right after the user schedules (or a tile posts/gets cancelled). Silent on
  // failure — the badge just doesn't show.
  const [scheduledCount, setScheduledCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const rows = await listScheduledPosts({ start: now });
        if (cancelled) return;
        const slots = new Set<string>();
        for (const p of rows) {
          const upcoming =
            (p.status === 'pending' || p.status === 'generating' || p.status === 'publishing') &&
            new Date(p.scheduled_at) >= now;
          if (upcoming) {
            slots.add(`${p.post_id ?? p.generated_post_id ?? p.id}|${p.scheduled_at}`);
          }
        }
        setScheduledCount(slots.size);
      } catch {
        /* silent — badge just doesn't render */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Derive the sidebar Connect-group rows from the canonical platform
  // list so this list can never drift from the schedule modal's chip
  // row. Display order = PLATFORM_PICK_ORDER (IG / FB / TikTok / X).
  const PLATFORMS = useMemo<PlatformDef[]>(
    () =>
      // TikTok + X are hidden from the sidebar until they're actually
      // supported (no OAuth / publish handlers yet). The scheduler's
      // PLATFORM_PICK_ORDER is left intact.
      PLATFORM_PICK_ORDER.filter((id) => id !== 'tiktok' && id !== 'x').map((id) => {
        const m = PLATFORM_META[id];
        return { id, name: m.label, icon: m.icon, color: m.color };
      }),
    [],
  );

  // Close the connect modal on Escape.
  useEffect(() => {
    if (!connectingTo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConnectingTo(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [connectingTo]);

  // Facebook connection state — drives the sidebar's Facebook row
  // FB / IG status now lives in the FacebookConnectionProvider so it
  // doesn't re-fetch on every route change (AppLayoutV2 used to do its
  // own getFacebookConnection() in a useEffect keyed on pathname, which
  // hit /api/auth/facebook on every page click). `refresh()` pulls a
  // fresh snapshot — call it after Disconnect or after a successful
  // OAuth bounce, NOT on every nav.
  const { status: fbStatus, refresh: refreshFbStatus } = useFacebookConnection();
  const [fbBusy, setFbBusy] = useState(false);
  const confirm = useConfirm();

  const handleFacebookConnect = async () => {
    if (fbBusy) return;
    setFbBusy(true);
    try {
      const { redirectUrl } = await startFacebookOAuth();
      // Full-page redirect to Meta. After OAuth, Meta sends the user
      // back to /api/auth/facebook/callback which 302s to /gallery?fb=
      // connected. The provider mounts once at app root and stays warm;
      // the OAuth callback page should call refreshFbStatus() to pull
      // the newly-saved connection into the cache (TODO: wire that into
      // the /gallery mount when ?fb=connected is present).
      window.location.assign(redirectUrl);
    } catch (err) {
      setFbBusy(false);
      console.error('[sidebar] FB connect failed:', err);
      alert(err instanceof Error ? err.message : 'Could not start Facebook connection.');
    }
  };

  const handleFacebookDisconnect = async () => {
    if (fbBusy || !fbStatus?.connected) return;
    const ok = await confirm({
      title: `Disconnect ${fbStatus.pageName || 'Facebook'}?`,
      message: (
        <>
          <p>Ad Astronaut will no longer be able to publish to this
          Facebook Page. You can reconnect any time.</p>
          <p className="mt-2 text-dark-muted text-xs">
            To fully revoke our app's access from Facebook's side, also
            visit facebook.com → Settings → Apps and Websites.
          </p>
        </>
      ),
      confirmLabel: 'Disconnect',
      destructive: true,
    });
    if (!ok) return;
    setFbBusy(true);
    try {
      await disconnectFacebook();
      await refreshFbStatus();
    } catch (err) {
      console.error('[sidebar] FB disconnect failed:', err);
      alert(err instanceof Error ? err.message : 'Could not disconnect Facebook.');
    } finally {
      setFbBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-surface">
      {/* SIDEBAR — responsive collapse + standard 3-zone layout (Jul 19).
          Sticky to viewport. The aside itself does NOT scroll
          (overflow-hidden); instead it's three flex zones:
            • PINNED TOP    — logo + brand switcher (flex-shrink-0)
            • SCROLLABLE    — the 3 nav groups (flex-1 min-h-0 overflow-y-auto)
            • PINNED BOTTOM — Credits + Account + Sign out (flex-shrink-0)
          So on short laptop screens ONLY the nav list scrolls — the brand
          switcher + account + sign-out anchors stay visible, never clipped
          (the whole-column overflow-y-auto that scrolled Sign out off-screen
          was the "weak" behavior we replaced). This is the standard sidebar
          pattern (Slack/Linear/Notion/GitHub). overscroll-contain on the
          middle stops scroll momentum leaking to the main column. */}
      {/* Mobile drawer backdrop — only when expanded AND below lg (where the
          aside is a fixed overlay). Tap it to collapse. Sits under the aside
          (z-40) and over the page content (z-30). Hidden on lg+ where the
          sidebar pushes content in-flow instead of overlaying. */}
      {navExpanded && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setNavExpanded(false)}
          aria-hidden="true"
        />
      )}
      <aside
        data-expanded={navExpanded ? 'true' : 'false'}
        className="
          group sticky top-0 left-0 z-40 h-screen flex-shrink-0 flex flex-col
          bg-surface-sidebar sidebar-right-stroke
          w-0 lg:w-16 data-[expanded=true]:w-[274px]
          data-[expanded=true]:fixed data-[expanded=true]:shadow-2xl data-[expanded=true]:shadow-black/20
          lg:data-[expanded=true]:sticky lg:data-[expanded=true]:shadow-none
          transition-[width] duration-200
          overflow-hidden
        "
      >
        {/* Logo + open/close toggle. The collapsed rail shows ONLY the
            hamburger (centered) so there's always a way to reveal the labels;
            expanded shows the wordmark on the left + a fold button on the
            right. Drives the aside's data-expanded via navExpanded. */}
        <div className="h-14 flex-shrink-0 flex items-center px-2.5 gap-2.5 justify-center group-data-[expanded=true]:justify-between">
          <a href="/" className="hidden group-data-[expanded=true]:flex items-center flex-shrink-0">
            <Logo height={50} />
          </a>
          <button
            type="button"
            onClick={() => setNavExpanded((v) => !v)}
            aria-label={navExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            title={navExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            className="w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center text-dark-muted hover:text-primary hover:bg-surface-dark/40 transition-colors cursor-pointer"
          >
            <i className="ri-menu-line text-xl group-data-[expanded=true]:hidden" />
            <i className="ri-menu-fold-line text-xl hidden group-data-[expanded=true]:inline" />
          </button>
        </div>

        {/* ============ BRAND SWITCHER ============ */}
        {/* Active brand selector. Sits above the Create group because every
            row below scopes to whatever brand is active here. Click to open
            a dropdown of all brands + "Manage brands →" link to /brands. */}
        <div className="flex-shrink-0 px-2 pt-1 pb-1">
          <BrandSwitcher expanded={navExpanded} onManage={() => navigate('/brands')} />
        </div>

        {/* ── SCROLLABLE MIDDLE ─────────────────────────────────────────
            Only the nav groups scroll when the viewport is too short —
            the logo + brand switcher (above) and Account + Sign out
            (below) stay pinned so they never scroll off-screen. The
            `min-h-0` is required: a flex child defaults to min-height:auto
            and won't shrink below its content, so without it this zone
            wouldn't scroll — it'd push the footer out instead. */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain sidebar-scroll">
        {/* ============ CREATE GROUP ============ */}
        {/* Section 1: the actual content-production surfaces. "+ New Creative"
            stays visible on every route (incl. /create — self-navigates,
            harmless) so the sidebar shape is invariant. Then the three
            user-facing surfaces: My Creatives (saved output), Templates
            (browse pre-baked), Ad Calendar (schedule). */}
        <div className="flex-shrink-0 px-2 pt-2 pb-1 space-y-1">
          <div className="hidden group-data-[expanded=true]:block px-2 pb-1">
            <span className="text-[10px] uppercase tracking-wider font-bold text-dark-muted/70">
              Create
            </span>
          </div>
          {/* Jun 26 — My Creatives is now the top CTA (was New Creative).
              Goal: drive more traffic into the saved-creatives surface,
              since that's where users spend most of their time. Solid
              primary pill + shadow + hover lift matches the look the
              "New Creative" button used to own. When on /gallery, a
              subtle ring around the icon tile signals "you're here"
              without compromising the always-on CTA feel. */}
          <button
            onClick={handleGallery}
            title="My Creatives"
            aria-current={activePath === '/gallery' ? 'page' : undefined}
            className="
              group flex items-center justify-center rounded-full cursor-pointer
              w-10 h-10 mx-auto
              group-data-[expanded=true]:w-full group-data-[expanded=true]:h-auto group-data-[expanded=true]:py-2 group-data-[expanded=true]:px-2
              group-data-[expanded=true]:justify-start group-data-[expanded=true]:gap-2.5
              bg-primary hover:bg-primary-hover text-white
              text-sm font-bold transition-all duration-200
              shadow-md shadow-primary/25
              hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5
            "
          >
            <div className={`
              w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full
              ${activePath === '/gallery' ? 'bg-white/25 ring-2 ring-white/40' : ''}
            `}>
              <i className="ri-gallery-line text-base" />
            </div>
            <span className="hidden group-data-[expanded=true]:inline">My Creatives</span>
          </button>
          {/* Extra breathing room between the two CTA pills (space-y-1
              gives only 4px; this spacer widens the gap to ~14px). */}
          <div aria-hidden="true" className="h-1.5" />
          {/* New Creative — Jul 14: outlined magenta CTA pill matching the
              My Creatives shape/color, so the starting point pops out. */}
          <SidebarAction
            icon="ri-add-line"
            label="New Creative"
            onClick={handleNewPost}
            active={false}
            cta
          />
          <SidebarAction
            icon="ri-layout-masonry-line"
            label="Templates"
            active={activePath === '/templates'}
            onClick={handleTemplates}
          />
          <SidebarAction
            icon="ri-flight-takeoff-line"
            label="Ad Pilot"
            active={activePath === '/schedule/auto'}
            onClick={handleAdPilot}
            pro={!isPremiumTier}
            comingSoon={!SCHEDULING_ENABLED}
          />
          <SidebarAction
            icon="ri-calendar-line"
            label="Ad Calendar"
            active={activePath === '/schedule'}
            onClick={handleSchedule}
            badgeCount={SCHEDULING_ENABLED ? scheduledCount : undefined}
            comingSoon={!SCHEDULING_ENABLED}
          />
          <SidebarAction
            icon="ri-radar-line"
            label="Ad Strategist"
            active={activePath === '/ad-strategist-v1'}
            onClick={handleAdStrategistV1}
            pro={!isPremiumTier}
          />
        </div>

        {/* ============ BRAND & INTEL GROUP ============ */}
        {/* Section 2: the user's saved data + competitive intelligence
            surfaces. Top border + small uppercase header (hidden in the
            collapsed rail) match the Connect group's pattern below. */}
        <div className="flex-shrink-0 mt-3 pt-3 px-2 border-t border-surface-dark/30 space-y-1">
          <div className="hidden group-data-[expanded=true]:block px-2 pb-1">
            <span className="text-[10px] uppercase tracking-wider font-bold text-dark-muted/70">
              Brand &amp; Intel
            </span>
          </div>
          <SidebarAction
            icon="ri-palette-line"
            label="My Brand"
            active={activePath === '/brand'}
            onClick={handleBrand}
          />
          <SidebarAction
            icon="ri-spy-line"
            label="My Competitors"
            active={activePath === '/competitors'}
            onClick={() => navigate('/competitors')}
          />
          <SidebarAction
            icon="ri-group-2-line"
            label="UGC Finder"
            active={activePath === '/ugc-finder'}
            onClick={() => navigate('/ugc-finder')}
            pro={!isPremiumTier}
          />
          <SidebarAction
            icon="ri-shopping-bag-3-line"
            label="My Products"
            active={activePath === '/products'}
            onClick={handleProducts}
          />
        </div>

        {/* ============ SOCIAL MEDIA ACCOUNTS GROUP ============ */}
        {/* Section 3: platform-connect rows. Each opens a "Coming soon"
            modal for v1; real OAuth lands later. Top border separates
            from Brand & Intel; bottom spacer (flex-1 below) pushes the
            Account group to the sidebar bottom. */}
        <div className="flex-shrink-0 mt-3 pt-3 px-2 border-t border-surface-dark/30 space-y-0.5">
          <div className="hidden group-data-[expanded=true]:block px-2 pb-1">
            <span className="text-[10px] uppercase tracking-wider font-bold text-dark-muted/70">
              Social Media Accounts
            </span>
          </div>
          {PLATFORMS.map((p) => {
            // V1 no-social (SOCIAL_PUBLISHING_ENABLED=false): every platform
            // renders as "coming soon" — no OAuth, no analytics. Click still
            // opens the coming-soon explainer modal. Flip the flag to restore
            // the connect/analytics branches below.
            if (!SOCIAL_PUBLISHING_ENABLED) {
              return (
                <SidebarConnectAction
                  key={p.id}
                  platform={p}
                  comingSoon
                  onClick={() => setConnectingTo(p)}
                />
              );
            }
            // Facebook — when connected, click opens the analytics page
            // (per-post stats + disconnect button). When NOT connected,
            // click starts the OAuth flow. Disconnect moved off the
            // sidebar so the primary use of the row is "see my stats."
            if (p.id === 'facebook') {
              return (
                <SidebarConnectAction
                  key={p.id}
                  platform={p}
                  connected={!!fbStatus?.connected}
                  connectedLabel={fbStatus?.pageName ?? undefined}
                  busy={fbBusy}
                  onClick={
                    fbStatus?.connected
                      ? () => navigate('/analytics/facebook')
                      : handleFacebookConnect
                  }
                />
              );
            }
            // Instagram (June 6) — discovered via the FB Page connection.
            // Same connected-vs-not click semantics as Facebook above:
            //   - connected     → /analytics/instagram
            //   - !connected    → trigger FB OAuth (grants IG scopes
            //                     in the same flow; IG auto-populates
            //                     from the Page if linked + Business)
            if (p.id === 'instagram') {
              const igConnected = !!fbStatus?.instagramUsername;
              return (
                <SidebarConnectAction
                  key={p.id}
                  platform={p}
                  connected={igConnected}
                  connectedLabel={
                    fbStatus?.instagramUsername
                      ? `@${fbStatus.instagramUsername}`
                      : undefined
                  }
                  busy={fbBusy}
                  onClick={
                    igConnected
                      ? () => navigate('/analytics/instagram')
                      : handleFacebookConnect
                  }
                />
              );
            }
            return (
              <SidebarConnectAction
                key={p.id}
                platform={p}
                onClick={() => setConnectingTo(p)}
              />
            );
          })}
        </div>

        </div>
        {/* ── /SCROLLABLE MIDDLE. The flex-1 above pushes the pinned
            Account group below to the sidebar bottom. ── */}

        {/* ============ ACCOUNT GROUP ============ */}
        <div className="flex-shrink-0 p-2 space-y-0.5">
          {/* Credits chip — gradient accent→primary bg, prominent balance,
              eyebrow plan label. */}
          <button
            type="button"
            onClick={() => navigate('/checkout')}
            className="
              glass-stroke flex items-center justify-center mb-2 cursor-pointer
              w-10 h-10 mx-auto rounded-full
              group-data-[expanded=true]:w-full group-data-[expanded=true]:h-auto group-data-[expanded=true]:py-2.5 group-data-[expanded=true]:rounded-2xl
              group-data-[expanded=true]:justify-start group-data-[expanded=true]:gap-2.5 group-data-[expanded=true]:px-2.5
              bg-gradient-to-br from-accent-light via-accent-light to-primary-light
              shadow-md shadow-accent/10 transition hover:shadow-lg hover:shadow-accent/20
            "
            title={isTrialing
              ? `${planName} trial · ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left · ${creditsLoading || balance === null ? '—' : balance} credits`
              : `${planLabel} · ${creditsLoading || balance === null ? '—' : balance} credits — buy more`}
          >
            <div className="w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center text-accent group-data-[expanded=true]:bg-white/80">
              <i className="ri-copper-coin-line text-base" />
            </div>
            <div className="hidden group-data-[expanded=true]:flex flex-col min-w-0">
              <span className="text-[10px] uppercase tracking-wide font-bold text-accent/80 truncate">
                {planLabel}
              </span>
              <span className="text-base font-bold text-dark leading-tight">
                {creditsLoading || balance === null ? '—' : balance}
                <span className="ml-1 text-xs font-semibold text-dark-muted">credits</span>
              </span>
            </div>
          </button>

          <SidebarAction
            icon="ri-user-3-line"
            label="Account"
            active={activePath === '/auth/profile'}
            onClick={() => navigate('/auth/profile')}
          />
          {/* Sign out — sits directly under Account (no separator) to save
              vertical space; the red-tinted hover already flags it. */}
          <SidebarAction
            icon="ri-logout-box-r-line"
            label="Sign out"
            onClick={() => signOut()}
            danger
          />
        </div>
      </aside>

      {/* MAIN — no top bar, content uses the full vertical viewport.
          Children supply their own max-w wrapper + padding. */}
      <main className="flex-1 min-w-0 relative">
        {/* Mobile top bar — on <lg the sidebar is a hidden overlay drawer, so
            this slim bar carries the hamburger to open it (freeing the full
            width for content). On lg+ the in-flow icon rail is visible, so
            this is hidden. Sticky so it stays reachable while scrolling. */}
        <div className="lg:hidden sticky top-0 z-20 flex items-center gap-2 h-14 px-3 bg-surface/95 backdrop-blur-sm border-b border-surface-dark/30">
          <button
            type="button"
            onClick={() => setNavExpanded(true)}
            aria-label="Open menu"
            className="w-10 h-10 rounded-lg flex items-center justify-center text-dark hover:bg-surface-dark/40 transition-colors cursor-pointer"
          >
            <i className="ri-menu-line text-2xl" />
          </button>
          <a href="/" className="flex items-center">
            <Logo height={38} />
          </a>
        </div>
        {children}
      </main>

      {/* Platform-connect "coming soon" modal. Reuses the warning-modal
          treatment from /appv2's gallery-exit confirm. */}
      {connectingTo && (
        <ConnectPlatformModal
          platform={connectingTo}
          onClose={() => setConnectingTo(null)}
        />
      )}
    </div>
  );
}


// =============================================================================
// BrandSwitcher — active brand chip + dropdown of all brands. Sits at the
// top of the sidebar above every brand-scoped surface (Create / Brand &
// Intel / Social Accounts groups all read from useBrand().activeBrand).
//
// Collapsed rail: shows just an avatar tile (initials or logo). Click opens
// the dropdown as a floating panel beside the rail.
// Full width: avatar + brand name + chevron, dropdown drops directly below.
//
// Activation is server-side (POST /api/brands/:id/activate); useBrand
// refetches afterward so every downstream hook that keys off activeBrand.id
// re-fires automatically.
// =============================================================================

function BrandSwitcher({ expanded, onManage }: { expanded: boolean; onManage: () => void }) {
  const { brands, activeBrand, loading, switchBrand } = useBrand();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Jun 26 — dropdown is rendered via portal (escapes sidebar's
  // overflow-y-auto clip) and positioned via getBoundingClientRect.
  // Recomputed on open, scroll, and resize so collapsed-rail users
  // get a working dropdown popped out to the right of the rail.
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const recomputePos = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    if (expanded) {
      // Expanded sidebar: drop BELOW the button, aligned to button left,
      // matching button width (so the panel sits over the sidebar column).
      // Keyed off the sidebar's OWN open state (not a viewport breakpoint) —
      // on mobile the drawer can be expanded at 390px, where a right-flyout
      // would run off-screen.
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    } else {
      // Collapsed rail: pop OUT to the right of the rail, aligned to
      // button top, fixed 256px width. Without this, the dropdown was
      // clipped by the aside's overflow-y-auto and users couldn't
      // click any of the brand rows.
      setPos({ top: rect.top, left: rect.right + 8, width: 256 });
    }
  };

  useEffect(() => {
    if (!open) return;
    recomputePos();
    const onDocClick = (e: MouseEvent) => {
      // Click is "inside" if it lands on either the trigger or the
      // portaled dropdown — both must be in the allowlist or
      // dropdown clicks would close the dropdown before the brand-row
      // click handler fires.
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScrollOrResize = () => recomputePos();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, expanded]);

  const handleSelect = async (brandId: string) => {
    if (brandId === activeBrand?.id) { setOpen(false); return; }
    setSwitching(true);
    try {
      // switchBrand triggers window.location.assign — the page navigates
      // away before this resolves. setSwitching stays true so the button
      // shows a spinner until reload kicks in.
      await switchBrand(brandId);
    } catch (err) {
      console.error('[BrandSwitcher] switch failed:', err);
      setSwitching(false);
    }
  };

  // Avatar content: brand initials (first letter or two of the name).
  const initials = (activeBrand?.name || 'My Brand')
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() || '')
    .join('') || 'B';

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        onClick={() => setOpen(o => !o)}
        title={activeBrand?.name || 'Select brand'}
        disabled={loading || switching}
        className={`
          group w-full flex items-center justify-center py-2 rounded-xl text-left
          group-data-[expanded=true]:justify-start group-data-[expanded=true]:gap-2.5 group-data-[expanded=true]:px-2
          transition-colors cursor-pointer
          ${open ? 'bg-surface' : 'hover:bg-surface'}
          ${(loading || switching) ? 'opacity-60 cursor-wait' : ''}
        `}
      >
        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-light text-primary font-bold text-sm overflow-hidden">
          {/* Jun 15 — logoUrl → favicon (Google S2) → initials. */}
          <BrandThumbnailImg brand={activeBrand ?? null} fallbackInitials={initials} />
        </div>
        <div className="hidden group-data-[expanded=true]:flex flex-col min-w-0 flex-1">
          <span className="text-[10px] uppercase tracking-wider font-bold text-dark-muted/70 leading-none">
            Brand
          </span>
          <span className="text-sm font-bold text-dark truncate leading-tight">
            {loading ? 'Loading…' : (activeBrand?.name || 'No brand')}
          </span>
        </div>
        <i className={`hidden group-data-[expanded=true]:inline ri-arrow-down-s-line text-base text-dark-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && pos && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            zIndex: 9999,
          }}
          className="rounded-xl bg-white border border-surface-dark/40 shadow-lg shadow-primary/10 p-1"
        >
          <div className="max-h-64 overflow-y-auto">
            {brands.length === 0 && (
              <div className="px-3 py-2 text-xs text-dark-muted">No brands yet.</div>
            )}
            {brands.map(b => (
              <button
                key={b.id}
                onClick={() => handleSelect(b.id)}
                className={`
                  w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm
                  transition-colors cursor-pointer
                  ${b.isActive ? 'bg-primary-light text-primary' : 'hover:bg-surface text-dark'}
                `}
              >
                <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-light text-primary font-bold text-[11px] overflow-hidden">
                  <BrandThumbnailImg
                    brand={b}
                    fallbackInitials={
                      b.name.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || 'B'
                    }
                  />
                </div>
                <span className="truncate flex-1 font-bold">{b.name}</span>
                {b.isActive && <i className="ri-check-line text-base flex-shrink-0" />}
              </button>
            ))}
          </div>
          <div className="border-t border-surface-dark/30 mt-1 pt-1">
            <button
              onClick={() => { setOpen(false); onManage(); }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm hover:bg-surface text-dark transition-colors cursor-pointer"
            >
              <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-dark-muted">
                <i className="ri-settings-3-line text-base" />
              </div>
              <span className="font-bold">Manage brands</span>
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}


// =============================================================================
// SidebarAction — generic icon + label row used by Workspace + Account
// groups. Stays compact in the collapsed rail (icon-only) and expands to
// icon + label at full sidebar width.
// =============================================================================

interface SidebarActionProps {
  icon: string;
  label: string;
  onClick: () => void;
  /** Tints the row red on hover — for destructive actions like sign-out. */
  danger?: boolean;
  /** When true, render the row in "you are here" state — primary-light
   *  background tile + primary-colored text/icon. Distinct from the
   *  "+ New Creative" pill above (which is solid primary regardless of route)
   *  so users can tell where they are AND see the always-on CTA at the
   *  same time. */
  active?: boolean;
  /** Render a small unread dot at the top-right of the icon tile. Used by
   *  the Ad Strategist row to flag freshly-completed scheduled re-runs. */
  badge?: boolean;
  /** Render a small numeric count pill at the top-right of the icon tile.
   *  Used by the Ad Calendar row to show how many posts are scheduled. Hidden
   *  when 0/undefined; caps at "9+". */
  badgeCount?: number;
  /** Render a small amber "WIP" pill next to the label. Used for
   *  surfaces that are in-development but already navigable, so users
   *  know to expect rough edges. */
  wip?: boolean;
  /** Jul 24 — render a small primary "PRO" pill next to the label. For
   *  premium surfaces shown to lower tiers so they can see + explore the
   *  feature's landing page, with the pill signalling it needs an upgrade. */
  pro?: boolean;
  /** Jul 14 — render a neutral "Coming soon" pill AND disable the row
   *  (muted, no hover, non-clickable). For surfaces not wired up yet, so
   *  users see them on the roadmap but can't navigate in. */
  comingSoon?: boolean;
  /** Jun 26 — sticky highlight. Row stays visually elevated regardless
   *  of route (subtle primary-tinted background + bolder text + accent
   *  left bar). Used for the user's primary destination (My Creatives)
   *  so it always reads as the "home base" without overpowering the
   *  active-route treatment. Active state still wins visually when on
   *  the matching route. */
  featured?: boolean;
  /** Jul 14 — starting-point CTA treatment. Outlined counterpart of the
   *  My Creatives pill: same primary magenta + rounded-full shape, but a
   *  stroke instead of a solid fill, with a filled magenta icon tile so it
   *  punches out from the plain nav rows without duplicating My Creatives. */
  cta?: boolean;
}

function SidebarAction({ icon, label, onClick, danger, active, badge, badgeCount, wip, pro, comingSoon, featured, cta }: SidebarActionProps) {
  // When this row becomes the active route, scroll it into view inside the
  // sidebar's scroll container. Fixes the low-res case where the active item
  // sits below the fold after navigation (block:'nearest' = minimal scroll,
  // no-op when already visible, and scoped to the nearest scrollable ancestor
  // so it never yanks the main content).
  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (active) btnRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  return (
    <button
      ref={btnRef}
      onClick={comingSoon ? undefined : onClick}
      disabled={comingSoon}
      title={comingSoon ? `${label} (Coming soon)` : wip ? `${label} (Work in progress)` : label}
      className={`
        relative flex items-center justify-center text-left
        ${cta
          ? 'w-10 h-10 mx-auto rounded-full group-data-[expanded=true]:w-full group-data-[expanded=true]:h-auto group-data-[expanded=true]:py-2'
          : 'w-full py-2 rounded-xl'}
        group-data-[expanded=true]:justify-start group-data-[expanded=true]:gap-2.5 group-data-[expanded=true]:px-2
        transition-all ${comingSoon ? 'cursor-not-allowed' : 'cursor-pointer'}
        ${comingSoon
          ? 'text-dark-muted/60'
          : cta
          // Starting-point CTA — outlined counterpart of the My Creatives
          // pill: same primary magenta + rounded-full shape, but a stroke
          // instead of a solid fill so the two pills read distinctly.
          ? 'border-2 border-primary/50 bg-primary-light/40 text-primary hover:border-primary hover:bg-primary-light/70 hover:-translate-y-px shadow-sm shadow-primary/15'
          : active
          // Clear active treatment — pale-pink fill was too subtle. Now: a
          // primary left accent bar + a ring for definition, plus a solid
          // magenta icon tile below (reads even in the collapsed icon-rail).
          ? "bg-primary-light text-primary ring-1 ring-primary/30 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-7 before:w-[3px] before:rounded-r-full before:bg-primary"
          : featured
            // Featured + not active — subtle primary-tinted background +
            // primary-colored text/icon. Less loud than the active state
            // (no white icon tile, lighter bg) but distinguishable from
            // default rows so it reads as the user's home base.
            ? 'bg-primary-light/40 text-primary hover:bg-primary-light/60'
            : danger
              ? 'text-dark hover:bg-red-50 hover:text-red-500'
              : 'text-dark hover:bg-surface'}
      `}
    >
      {/* Icon tile — in the active state, gets a solid white square so
          the icon glyph sits in a clear focal element against the
          primary-light row background. Inactive icons sit flush in the
          row (no tile). */}
      <div className={`
        relative w-9 h-9 flex-shrink-0 flex items-center justify-center
        ${cta ? 'rounded-full' : 'rounded-lg'}
        transition-colors
        ${cta
          ? 'text-primary group-data-[expanded=true]:bg-primary group-data-[expanded=true]:text-white group-data-[expanded=true]:shadow-sm group-data-[expanded=true]:shadow-primary/30'
          : active ? 'bg-primary text-white shadow-sm shadow-primary/30' : ''}
      `}>
        <i className={`${icon} text-base`} />
        {badge && (
          <span
            className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-primary shadow-sm shadow-primary/40 ring-2 ring-white"
            aria-label="New activity"
          />
        )}
        {!badge && typeof badgeCount === 'number' && badgeCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-primary text-white text-[10px] font-bold leading-none shadow-sm shadow-primary/40 ring-2 ring-white"
            aria-label={`${badgeCount} scheduled`}
          >
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </div>
      <span className="hidden group-data-[expanded=true]:inline-flex items-center gap-1.5 text-sm font-bold truncate min-w-0">
        <span className="truncate">{label}</span>
        {comingSoon ? (
          <span
            className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-dark-muted/15 text-dark-muted"
            title="Coming soon"
          >
            Coming soon
          </span>
        ) : wip ? (
          <span
            className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700"
            title="Work in progress"
          >
            WIP
          </span>
        ) : pro ? (
          <span
            className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-primary-light text-primary"
            title="Pro feature — upgrade to unlock"
          >
            PRO
          </span>
        ) : null}
      </span>
    </button>
  );
}


// =============================================================================
// SidebarConnectAction — row in the Connect group. Same shape as
// SidebarAction but uses the platform's brand color for the icon and
// shows a "Connect" pill at the right at full width.
// =============================================================================

function SidebarConnectAction({
  platform,
  onClick,
  connected = false,
  connectedLabel,
  busy = false,
  comingSoon = false,
}: {
  platform: PlatformDef;
  onClick: () => void;
  /** When true the pill flips from "Connect" to a green "Connected" badge
   *  and the row's tooltip becomes the disconnect action. Used for
   *  platforms with shipped OAuth (Facebook as of June 6). */
  connected?: boolean;
  /** Page / account name to surface under the platform name when connected
   *  (e.g. "My Coffee Shop"). Truncated visually. */
  connectedLabel?: string;
  /** Disables the click + dims the pill while an OAuth start / disconnect
   *  call is in flight. Prevents double-fires during the ~50ms RTT. */
  busy?: boolean;
  /** V1 no-social — render a neutral "Soon" pill instead of "Connect".
   *  Click still opens the coming-soon explainer modal. */
  comingSoon?: boolean;
}) {
  const title = comingSoon
    ? `${platform.name} — coming soon`
    : connected
    ? `Click to disconnect ${connectedLabel || platform.name}`
    : `Connect to ${platform.name}`;
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={busy}
      className="
        w-full flex items-center justify-center py-2 rounded-xl text-left
        group-data-[expanded=true]:justify-start group-data-[expanded=true]:gap-2.5 group-data-[expanded=true]:px-2
        transition-colors cursor-pointer text-dark
        hover:bg-surface
        disabled:opacity-60 disabled:cursor-not-allowed
      "
    >
      <div
        className="relative w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${platform.color}18` }}
      >
        <i
          className={`${platform.icon} text-base`}
          style={{ color: platform.color }}
        />
        {/* Connected dot — small green pip at top-right of the icon tile,
            mirrors the unread-dot pattern on the Ad Strategist row. */}
        {connected && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="hidden group-data-[expanded=true]:flex flex-col flex-1 min-w-0">
        <span className="text-sm font-bold truncate">{platform.name}</span>
        {connected && connectedLabel && (
          <span className="text-[10px] text-dark-muted truncate">
            {connectedLabel}
          </span>
        )}
      </div>
      <span
        className={
          'hidden group-data-[expanded=true]:inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ' +
          (comingSoon
            ? 'border border-surface-dark/40 text-dark-muted/70'
            : connected
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'border border-surface-dark/40 text-dark-muted')
        }
      >
        {comingSoon ? 'Soon' : connected ? 'Connected' : 'Connect'}
      </span>
    </button>
  );
}


// =============================================================================
// ConnectPlatformModal — "Coming soon" explainer for direct publishing.
// Reuses the warning-modal treatment from /appv2's gallery-exit confirm.
// Future phase will replace this with a real OAuth flow per platform.
// =============================================================================

function ConnectPlatformModal({
  platform,
  onClose,
}: {
  platform: PlatformDef;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-md w-full shadow-xl"
        // Stop clicks inside the modal from bubbling to the backdrop's
        // dismiss handler.
        onClick={(e) => e.stopPropagation()}
      >
        {/* Platform brand-colored icon header. */}
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
          style={{ backgroundColor: `${platform.color}18` }}
        >
          <i
            className={`${platform.icon} text-3xl`}
            style={{ color: platform.color }}
          />
        </div>
        <h3 className="font-display font-bold text-dark text-lg mb-2">
          Connect to {platform.name}
        </h3>
        <p className="text-dark-muted text-sm mb-4">
          Once {platform.name} is connected, you'll be able to publish your
          generated posts directly to your feed without leaving Ad Astronaut —
          no copy-paste, no re-uploads, no scheduling tools required.
        </p>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-6">
          <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
            <i className="ri-time-line text-amber-500 mt-0.5 flex-shrink-0" />
            Coming soon — we're working with {platform.name}'s API team to
            enable direct publishing. We'll notify you when it's live.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// BrandThumbnailImg — Jun 15
// logoUrl → favicon (Google S2 via brandThumbnailUrl) → initials.
// onError fall-through so a 404 favicon downgrades to initials instead
// of a broken-image glyph. Used by both the BrandSwitcher button and
// the dropdown items.
// =============================================================================

// Brand tile = single-letter monogram on the brand's own color. Color logic
// lives in @/lib/brandThumbnail (brandAvatarStyle) so the /brands manager
// cards render identically. We deliberately DON'T render the scraped logo
// here: brand logos are usually wide wordmarks (e.g. FENTY BEAUTY at 1024×56)
// that crop to an unreadable sliver in a small square tile. (Jul 23)
function BrandThumbnailImg({
  brand,
  fallbackInitials,
}: {
  brand: { name?: string | null; colors?: string[] | null } | null;
  fallbackInitials: string;
}) {
  const { letter, bg, fg } = brandAvatarStyle(brand, fallbackInitials);
  return (
    <div
      className="w-full h-full flex items-center justify-center font-bold"
      style={{ backgroundColor: bg, color: fg }}
    >
      {letter}
    </div>
  );
}
